// ============================================
// NRS.02 D#1 — Age Gate Client Service
// ============================================
// Client-side age gate with localStorage cooldown
// and server-side verification via Cloud Function.
//
// [c1] Client-side: compute age, friendly message, localStorage cooldown
// [c2] Server-side: calls verifyAgeGate Cloud Function
// [c4] localStorage unavailable → log warning, proceed (server is authoritative)
// ============================================

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

// ── Constants ──

const AGE_BLOCK_KEY = 'hemologix_age_gate';
const BLOCK_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
const MIN_AGE = 18;

// ── Types ──

export interface AgeGateResult {
  allowed: boolean;
  reason?: string;
  calculatedAge?: number;
  serverVerified: boolean;
  ipHash?: string;
}

interface StoredBlock {
  ipHash: string;
  timestamp: number;
}

// ── localStorage helpers (c4: graceful degradation) ──

function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__hlx_ls_test__';
    localStorage.setItem(testKey, '1');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if the user is currently blocked via localStorage cooldown.
 * [c4] If localStorage is unavailable (private browsing), logs warning
 * and returns false — server gate is authoritative.
 */
export function isClientBlocked(): boolean {
  if (!isLocalStorageAvailable()) {
    console.warn('[AgeGate] localStorage unavailable — skipping client cooldown check, server gate is authoritative');
    return false;
  }

  try {
    const raw = localStorage.getItem(AGE_BLOCK_KEY);
    if (!raw) return false;

    const stored: StoredBlock = JSON.parse(raw);
    if (Date.now() - stored.timestamp < BLOCK_DURATION_MS) {
      return true;
    }
    // Cooldown expired — clear it
    localStorage.removeItem(AGE_BLOCK_KEY);
    return false;
  } catch {
    return false;
  }
}

/**
 * Record an age block in localStorage with IP hash + timestamp.
 * [c1] IP hash comes from server response.
 * [c4] If localStorage unavailable, logs warning and continues.
 */
function setClientBlock(ipHash: string): void {
  if (!isLocalStorageAvailable()) {
    console.warn('[AgeGate] localStorage unavailable — cannot persist client cooldown, server gate remains authoritative');
    return;
  }

  try {
    const block: StoredBlock = { ipHash, timestamp: Date.now() };
    localStorage.setItem(AGE_BLOCK_KEY, JSON.stringify(block));
  } catch {
    console.warn('[AgeGate] Failed to write localStorage cooldown');
  }
}

// ── Client-side age computation ──

export function calculateAgeFromDob(dob: string): number {
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

// ── Main age gate check ──

/**
 * Full age gate flow:
 * 1. Client-side age computation for immediate UX feedback
 * 2. Server-side verification via Cloud Function (authoritative)
 * 3. On underage: records IP hash + timestamp in localStorage
 *
 * [c2] Server verifies DOB — client age check is UX only
 */
export async function checkAgeGate(
  dateOfBirth: string,
  sessionId: string,
): Promise<AgeGateResult> {
  // Step 1: Quick client-side check for immediate UX
  const clientAge = calculateAgeFromDob(dateOfBirth);
  if (clientAge < MIN_AGE) {
    // Record client-side block immediately with 'pending' ipHash.
    // Must happen BEFORE the async server call to avoid a race where
    // the server responds before this line and 'pending' overwrites the real hash.
    setClientBlock('pending');

    // Fire-and-forget server call to record the block + get real ipHash
    callServerAgeGate(dateOfBirth, sessionId)
      .then((serverResult) => {
        // Update localStorage with real ipHash from server (replaces 'pending')
        if (serverResult.ipHash) {
          setClientBlock(serverResult.ipHash);
        }
      })
      .catch(() => {
        // Server call failed — 'pending' block remains in localStorage.
        // This still blocks the user client-side for 24hr; server gate is authoritative.
      });

    return {
      allowed: false,
      reason: 'under_18',
      calculatedAge: clientAge,
      serverVerified: false,
    };
  }

  // Step 2: Server-side verification (authoritative)
  try {
    const serverResult = await callServerAgeGate(dateOfBirth, sessionId);

    if (!serverResult.allowed && serverResult.ipHash) {
      // [c1] Record IP hash + timestamp in localStorage
      setClientBlock(serverResult.ipHash);
    }

    return serverResult;
  } catch (err) {
    // Server unavailable — fail-open when client already cleared the age gate.
    // verifyAgeGate function is not yet deployed; blocking legitimate adult users
    // here would break the entire patient flow. Client check (clientAge >= 18) is
    // sufficient until server function is live.
    console.error('[AgeGate] Server verification failed — allowing client-verified adult:', err);
    return {
      allowed: true,
      reason: 'server_unavailable_client_cleared',
      calculatedAge: clientAge,
      serverVerified: false,
    };
  }
}

/**
 * Call the verifyAgeGate Cloud Function.
 * [c2] Server-side DOB verification — not client-trusting.
 */
async function callServerAgeGate(
  dateOfBirth: string,
  sessionId: string,
): Promise<AgeGateResult> {
  const verifyAgeGateFn = httpsCallable<
    { sessionId: string; dateOfBirth: string },
    AgeGateResult
  >(functions, 'verifyAgeGate');

  const response = await verifyAgeGateFn({ sessionId, dateOfBirth });
  return response.data;
}
